import { gruposDoFunil, FUNIL_CARREGADO_EVENT } from '../data/etapas';
import { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { LuCheck, LuClock, LuTriangleAlert, LuTarget } from 'react-icons/lu';
import { OpportunityService } from '../services/OpportunityService';
import type { Opportunity } from '../services/OpportunityService';
import RelatorioService from '../services/RelatorioService';
import type { PerformanceIndividual, PerformanceEquipe, FaturamentoMensal } from '../services/RelatorioService';
import GerenciarEmpresaService from '../services/GerenciarEmpresaService';
import type { DiasUteis } from '../services/GerenciarEmpresaService';
import { useChartTokens, useTooltipStyle } from '../components/ChartKit';
import { Link } from 'react-router-dom';
import './AnalyticsPage.css';

interface AnalyticsPageProps {
    selectedMonth?: string;
}

const DEFAULT_WORKING_DAYS: DiasUteis = {
    segunda: true, terca: true, quarta: true, quinta: true, sexta: true, sabado: false, domingo: false,
};
const DAY_NAMES: (keyof DiasUteis)[] = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];

/** Etapas que ainda estão em jogo. Ganho e Perda são desfechos, não etapas. */

const AnalyticsPage = ({ selectedMonth: propSelectedMonth }: AnalyticsPageProps) => {
    const [localMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const selectedMonth = propSelectedMonth || localMonth;

    const tokens = useChartTokens();
    const tooltip = useTooltipStyle();

    /* O funil chega uma vez por sessão (MainLayout). Quem abre esta tela antes disso precisa
       redesenhar quando ele chega, senão conta pelas colunas padrão. */
    const [funilVersao, setFunilVersao] = useState(0);
    useEffect(() => {
        const aoCarregar = () => setFunilVersao(v => v + 1);
        window.addEventListener(FUNIL_CARREGADO_EVENT, aoCarregar);
        return () => window.removeEventListener(FUNIL_CARREGADO_EVENT, aoCarregar);
    }, []);

    const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
    const [individualPerf, setIndividualPerf] = useState<PerformanceIndividual[]>([]);
    const [teamPerf, setTeamPerf] = useState<PerformanceEquipe[]>([]);
    const [monthlyGrowth, setMonthlyGrowth] = useState<FaturamentoMensal[]>([]);
    const [workingDays, setWorkingDays] = useState<DiasUteis>(DEFAULT_WORKING_DAYS);
    const [loadingOpportunities, setLoadingOpportunities] = useState(true);
    const [loadingRelatorios, setLoadingRelatorios] = useState(true);
    const isLoading = loadingOpportunities || loadingRelatorios;

    useEffect(() => {
        OpportunityService.getAllOpportunities()
            .then(data => setOpportunities(data || []))
            .catch(() => { })
            .finally(() => setLoadingOpportunities(false));
        GerenciarEmpresaService.getDiasUteis()
            .then(setWorkingDays)
            .catch(() => { /* mantém o padrão seg–sex */ });
    }, []);

    useEffect(() => {
        setLoadingRelatorios(true);
        Promise.all([
            RelatorioService.getPerformanceIndividual(selectedMonth)
                .then(setIndividualPerf).catch(() => setIndividualPerf([])),
            RelatorioService.getPerformanceEquipes(selectedMonth)
                .then(setTeamPerf).catch(() => setTeamPerf([])),
            RelatorioService.getFaturamentoMensal(6)
                .then(setMonthlyGrowth).catch(() => setMonthlyGrowth([])),
        ]).finally(() => setLoadingRelatorios(false));
    }, [selectedMonth]);

    const formatCurrency = (value: number) =>
        new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

    const formatCurrencyShort = (value: number) =>
        new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value);

    const totalRealized = useMemo(
        () => individualPerf.reduce((s, a) => s + a.totalFaturado, 0), [individualPerf]);

    const metaGeralTotal = useMemo(
        () => individualPerf.reduce((s, a) => s + a.metaFaturamento, 0), [individualPerf]);

    /* Dias ÚTEIS, não corridos — a projeção anterior dividia pelo dia do mês,
       o que inflava o ritmo em meses que começam no fim de semana. */
    const pace = useMemo(() => {
        const [year, month] = selectedMonth.split('-').map(Number);
        const now = new Date();
        const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;
        const today = now.getDate();

        let total = 0;
        let elapsed = 0;
        const cursor = new Date(year, month - 1, 1);
        while (cursor.getMonth() === month - 1) {
            if (workingDays[DAY_NAMES[cursor.getDay()]]) {
                total++;
                if (!isCurrentMonth || cursor.getDate() <= today) elapsed++;
            }
            cursor.setDate(cursor.getDate() + 1);
        }
        total = total || 1;
        elapsed = Math.max(elapsed, 1);

        const dailyAverage = totalRealized / elapsed;
        const requiredDaily = metaGeralTotal / total;
        const projection = isCurrentMonth ? dailyAverage * total : totalRealized;
        const expectedByNow = metaGeralTotal * (elapsed / total);

        return {
            isCurrentMonth, total, elapsed, remaining: Math.max(total - elapsed, 0),
            dailyAverage, requiredDaily, projection, expectedByNow,
        };
    }, [selectedMonth, totalRealized, metaGeralTotal, workingDays]);

    /* Escala do bullet: máx(realizado, meta) × 1,1. É o que impede a barra de
       saturar — 101% e 643% precisam ser desenhos diferentes. */
    const bullet = useMemo(() => {
        const scale = Math.max(totalRealized, metaGeralTotal) * 1.1 || 1;
        const pctOf = (v: number) => Math.max(0, Math.min(100, (v / scale) * 100));
        const achievement = metaGeralTotal > 0 ? (totalRealized / metaGeralTotal) * 100 : 0;
        const behind = pace.isCurrentMonth && totalRealized < pace.expectedByNow;
        return {
            fill: pctOf(totalRealized),
            nearZero: pctOf(totalRealized) < 6,
            metaAt: pctOf(metaGeralTotal),
            expectedAt: pctOf(pace.expectedByNow),
            achievement,
            behind,
            onTrack: !behind && totalRealized < metaGeralTotal,
            beat: totalRealized >= metaGeralTotal,
        };
    }, [totalRealized, metaGeralTotal, pace]);

    const achievementLabel = bullet.achievement >= 200
        ? `${(bullet.achievement / 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}× a meta`
        : `${Math.round(bullet.achievement)}% da meta`;

    /* As faixas são as colunas do funil da empresa — etapa que ela criou entra aqui sozinha. Antes
       isto era um switch de ids com `default: 'Novos'`, e qualquer etapa nova virava "Novos" no
       gráfico: número errado, sem ninguém perceber. */
    const stageTotals = useMemo(() => {
        const stages: Record<string, { count: number; value: number }> = {};
        gruposDoFunil().forEach(g => { stages[g.title] = { count: 0, value: 0 }; });

        opportunities.forEach(op => {
            const grupo = gruposDoFunil().find(g => g.statusIds.includes(op.idStauts));
            if (!grupo) return;
            stages[grupo.title].count++;
            stages[grupo.title].value += op.valor || 0;
        });
        return stages;
    }, [opportunities, funilVersao]);

    /* Em aberto é tudo que não é desfecho: o miolo, por maior que ele fique, mais os novos. */
    const openStages = useMemo(
        () => gruposDoFunil()
            .filter(g => g.cor !== 'win' && g.cor !== 'lost')
            .map(g => ({ name: g.title, ...(stageTotals[g.title] ?? { count: 0, value: 0 }) })),
        [stageTotals, funilVersao]);

    const tituloGanho = gruposDoFunil().find(g => g.cor === 'win')?.title ?? 'Ganho';
    const tituloPerdido = gruposDoFunil().find(g => g.cor === 'lost')?.title ?? 'Perdido';

    const openMax = useMemo(
        () => Math.max(...openStages.map(s => s.value), 1), [openStages]);

    const openTotal = useMemo(() => ({
        value: openStages.reduce((s, x) => s + x.value, 0),
        count: openStages.reduce((s, x) => s + x.count, 0),
    }), [openStages]);

    const teamsRanking = useMemo(
        () => [...teamPerf]
            .sort((a, b) => b.percentualAtingido - a.percentualAtingido)
            .map(t => ({
                id: t.equipeId,
                name: t.equipeNome,
                realized: t.totalFaturado,
                goal: t.metaFaturamento,
                pct: t.percentualAtingido,
                hasGoal: t.metaFaturamento > 0,
            })),
        [teamPerf]);

    const peopleRanking = useMemo(
        () => [...individualPerf]
            .sort((a, b) => b.percentualFaturamento - a.percentualFaturamento)
            .map(a => ({
                id: a.usuarioId,
                name: a.usuarioNome,
                realized: a.totalFaturado,
                goal: a.metaFaturamento,
                pct: a.percentualFaturamento,
                hasGoal: a.metaFaturamento > 0,
            })),
        [individualPerf]);

    /* Régua comum às duas listas: 100% fica sempre no mesmo lugar, então dá
       para comparar pessoa com equipe sem recalcular. */
    const rankingScale = useMemo(() => {
        const worst = Math.max(
            ...teamsRanking.map(t => t.pct),
            ...peopleRanking.filter(p => p.hasGoal).map(p => p.pct),
            100,
        );
        return Math.max(worst, 100) * 1.05;
    }, [teamsRanking, peopleRanking]);

    const growthData = useMemo(
        () => monthlyGrowth.map(m => ({ month: m.mesLabel, value: m.totalFaturado })), [monthlyGrowth]);

    const RankingBar = ({ pct, dim }: { pct: number; dim?: boolean }) => (
        <div className="an-bar-track">
            <div
                className="an-bar-fill"
                style={{
                    width: `${Math.min((pct / rankingScale) * 100, 100)}%`,
                    backgroundColor: dim ? tokens.ramp[1] : tokens.primary,
                }}
            />
            <div className="an-bar-goal" style={{ left: `${(100 / rankingScale) * 100}%` }} />
        </div>
    );

    type RankRow = { id: number; name: string; realized: number; goal: number; pct: number; hasGoal: boolean };

    /* Pessoas e equipes usam a MESMA tabela e a MESMA régua — é o que deixa
       comparar uma pessoa com uma equipe sem recalcular nada. */
    const RankingTable = ({ rows, nameLabel }: { rows: RankRow[]; nameLabel: string }) => (
        <div className="an-table">
            <div className="an-table-head an-table-row">
                <span>{nameLabel}</span>
                <span className="is-right">Faturado</span>
                <span className="is-right">Meta</span>
                <span>Atingimento</span>
                <span className="is-right">%</span>
            </div>
            {rows.map(row => (
                <div key={row.id} className="an-table-row">
                    <span className="an-table-name" title={row.name}>{row.name}</span>
                    <span className="is-right">{formatCurrencyShort(row.realized)}</span>
                    <span className="is-right is-muted">{row.hasGoal ? formatCurrencyShort(row.goal) : '—'}</span>
                    <RankingBar pct={row.hasGoal ? row.pct : 0} dim={!row.hasGoal} />
                    <span className="is-right an-table-pct">{row.hasGoal ? `${Math.round(row.pct)}%` : '—'}</span>
                </div>
            ))}
        </div>
    );

    if (isLoading) {
        return (
            <div className="analytics-container">
                <div className="an-hero chart-card">
                    <span className="skeleton skeleton-text-sm" style={{ width: '120px', marginBottom: '12px' }} />
                    <span className="skeleton" style={{ height: '40px', width: '240px', borderRadius: '8px', display: 'block', marginBottom: '20px' }} />
                    <span className="skeleton" style={{ height: '14px', borderRadius: '4px', display: 'block' }} />
                </div>
                <div className="an-row">
                    {[0, 1].map(i => (
                        <div key={i} className="chart-card">
                            <span className="skeleton skeleton-text-md" style={{ width: '180px', marginBottom: '18px' }} />
                            <div className="individual-ranking-list">
                                {[...Array(4)].map((_, j) => (
                                    <div key={j} className="skeleton-ranking-row">
                                        <span className="skeleton skeleton-text-sm" style={{ width: '90px', flexShrink: 0 }} />
                                        <span className="skeleton" style={{ flex: 1, height: '18px', borderRadius: '3px' }} />
                                        <span className="skeleton skeleton-text-sm" style={{ width: '40px', flexShrink: 0 }} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="chart-card">
                    <span className="skeleton skeleton-text-md" style={{ width: '160px', marginBottom: '18px' }} />
                    <span className="skeleton skeleton-chart" />
                </div>
            </div>
        );
    }

    return (
        <div className="analytics-container">

            {/* ═══ BLOCO HERÓI: A META ═══════════════════════════════ */}
            <section className="an-hero chart-card">
                {metaGeralTotal > 0 ? (
                    <>
                        <div className="an-hero-top">
                            <div className="an-hero-headline">
                                <span className="an-hero-label">Meta do mês</span>
                                <div className="an-hero-value-row">
                                    <strong className="an-hero-value">{formatCurrencyShort(totalRealized)}</strong>
                                    {bullet.beat && (
                                        <span className="an-chip an-chip-good">
                                            <LuCheck /> {achievementLabel}
                                        </span>
                                    )}
                                    {bullet.onTrack && (
                                        <span className="an-chip an-chip-info">
                                            <LuClock /> no ritmo
                                        </span>
                                    )}
                                    {bullet.behind && (
                                        <span className="an-chip an-chip-warn">
                                            <LuTriangleAlert /> abaixo do ritmo
                                        </span>
                                    )}
                                </div>
                                <span className="an-hero-sub">
                                    de {formatCurrencyShort(metaGeralTotal)}
                                    {pace.isCurrentMonth && ` · faltam ${pace.remaining} ${pace.remaining === 1 ? 'dia útil' : 'dias úteis'}`}
                                </span>
                            </div>

                            <div className="an-hero-stats">
                                <div className="an-stat">
                                    <span className="an-stat-label">Ritmo atual</span>
                                    <strong className={`an-stat-value ${bullet.behind ? 'is-warn' : ''}`}>
                                        {formatCurrencyShort(pace.dailyAverage)}<span className="an-stat-unit">/dia útil</span>
                                    </strong>
                                </div>
                                <div className="an-stat">
                                    <span className="an-stat-label">Necessário</span>
                                    <strong className="an-stat-value is-muted">
                                        {formatCurrencyShort(pace.requiredDaily)}<span className="an-stat-unit">/dia útil</span>
                                    </strong>
                                </div>
                                <div className="an-stat">
                                    <span className="an-stat-label">Projeção</span>
                                    <strong className={`an-stat-value ${pace.projection < metaGeralTotal ? 'is-warn' : ''}`}>
                                        {formatCurrencyShort(pace.projection)}
                                    </strong>
                                </div>
                            </div>
                        </div>

                        {/* bullet: a meta é um marcador, não o fim da barra */}
                        <div className="an-bullet">
                            <div className="an-bullet-track">
                                <div className="an-bullet-fill" style={{ width: `${bullet.fill}%` }} />
                                {bullet.behind && (
                                    <div
                                        className="an-bullet-expected"
                                        style={{ left: `${bullet.expectedAt}%` }}
                                        title={`Onde deveria estar hoje: ${formatCurrency(pace.expectedByNow)}`}
                                    />
                                )}
                                <div className="an-bullet-meta" style={{ left: `${bullet.metaAt}%` }} />
                            </div>
                            <div className="an-bullet-labels">
                                {/* realizado colado no zero: a tag dele ocupa o lugar do "R$ 0" */}
                                {!bullet.nearZero && <span className="an-bullet-zero">R$ 0</span>}
                                <span className="an-bullet-tag is-meta" style={{ left: `${bullet.metaAt}%` }}>
                                    meta {formatCurrencyShort(metaGeralTotal)}
                                </span>
                                {Math.abs(bullet.fill - bullet.metaAt) > 14 && (
                                    <span
                                        className={`an-bullet-tag is-value ${bullet.nearZero ? 'is-start' : ''}`}
                                        style={{ left: bullet.nearZero ? 0 : `${bullet.fill}%` }}
                                    >
                                        {formatCurrencyShort(totalRealized)}
                                    </span>
                                )}
                            </div>
                            {bullet.behind && (
                                <div className="an-bullet-legend">
                                    <span className="an-bullet-legend-dash" />
                                    onde deveria estar hoje, no {pace.elapsed}º dia útil — {formatCurrencyShort(pace.expectedByNow)}
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="empty-goals-state">
                        <LuTarget className="empty-goals-icon" />
                        <h4>Nenhuma meta cadastrada</h4>
                        <p>
                            Faturado no mês: <strong>{formatCurrency(totalRealized)}</strong>. Defina metas para
                            acompanhar ritmo, atingimento e projeção de fechamento.
                        </p>
                        <Link to="/metas" className="btn-create-goals">Cadastrar metas</Link>
                    </div>
                )}
            </section>

            {/* ═══ FUNIL, SOZINHO NA LINHA ═══════════════════════════ */}
            <section className="chart-card">
                <header className="an-card-head">
                    <h3>Onde o dinheiro está parado</h3>
                    <span className="an-card-meta">
                        {formatCurrencyShort(openTotal.value)} · {openTotal.count} {openTotal.count === 1 ? 'negócio' : 'negócios'}
                    </span>
                </header>

                <div className="an-list">
                    <div className="an-list-row an-list-head">
                        <span>Etapa</span>
                        <span>Valor parado</span>
                        <span className="is-right">Valor</span>
                        <span className="is-right">Negócios</span>
                    </div>
                    {openStages.map(stage => (
                        <div key={stage.name} className="an-list-row">
                            <span className="an-list-name">{stage.name}</span>
                            <div className="an-bar-track">
                                <div
                                    className="an-bar-fill"
                                    style={{
                                        width: `${(stage.value / openMax) * 100}%`,
                                        backgroundColor: tokens.primary,
                                    }}
                                />
                            </div>
                            <span className={`an-list-value ${stage.value === 0 ? 'is-empty' : ''}`}>
                                {stage.value > 0 ? formatCurrencyShort(stage.value) : 'vazia'}
                            </span>
                            <span className={`an-list-count ${stage.count === 0 ? 'is-empty' : ''}`}>
                                {stage.count}
                            </span>
                        </div>
                    ))}
                </div>

                <footer className="an-card-foot">
                    <span>Ganhos no mês <strong className="is-good">{stageTotals[tituloGanho]?.count ?? 0} · {formatCurrencyShort(stageTotals[tituloGanho]?.value ?? 0)}</strong></span>
                    <span>Perdidos <strong>{stageTotals[tituloPerdido]?.count ?? 0}</strong></span>
                </footer>
            </section>

            {/* ═══ PESSOAS + EQUIPES, LADO A LADO ════════════════════ */}
            <div className="an-row">
                <section className="chart-card">
                    <header className="an-card-head">
                        <h3>Pessoas</h3>
                        <span className="an-card-meta">atingimento de meta</span>
                    </header>

                    {peopleRanking.length > 0 ? (
                        <RankingTable rows={peopleRanking} nameLabel="Vendedor" />
                    ) : (
                        <p className="chart-empty">Nenhum dado individual para este mês.</p>
                    )}
                </section>

                <section className="chart-card">
                    <header className="an-card-head">
                        <h3>Equipes</h3>
                        <span className="an-card-meta">atingimento de meta</span>
                    </header>

                    {teamsRanking.length > 0 ? (
                        <RankingTable rows={teamsRanking} nameLabel="Equipe" />
                    ) : (
                        <p className="chart-empty">Nenhum dado de equipes para este mês.</p>
                    )}
                </section>
            </div>

            {/* ═══ CRESCIMENTO ═══════════════════════════════════════ */}
            <section className="chart-card">
                <header className="an-card-head">
                    <h3>Crescimento mensal</h3>
                    <span className="an-card-meta">últimos 6 meses</span>
                </header>

                {growthData.length > 0 ? (
                    <div className="an-chart">
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={growthData} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
                                <XAxis
                                    dataKey="month"
                                    stroke={tokens.axis}
                                    fontSize={11}
                                    axisLine={{ stroke: tokens.border }}
                                    tickLine={false}
                                />
                                <YAxis
                                    stroke={tokens.axis}
                                    fontSize={11}
                                    axisLine={false}
                                    tickLine={false}
                                    width={56}
                                    tickFormatter={v => formatCurrencyShort(v as number)}
                                />
                                <Tooltip
                                    formatter={(value: number) => [formatCurrency(value), 'Faturado']}
                                    {...tooltip}
                                />
                                <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={56}>
                                    {growthData.map((_, i) => (
                                        <Cell key={i} fill={i === growthData.length - 1 ? tokens.ramp[4] : tokens.primary} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                ) : (
                    <p className="chart-empty">Nenhum dado de faturamento disponível.</p>
                )}
            </section>
        </div>
    );
};

export default AnalyticsPage;
